import { size } from "lodash";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from "react";

import { SKUPageContentTabType } from "@/data/types";
import { useCMSStore } from "@/hooks/use-cms-store";

interface CMSTabContextType {
  tabType: SKUPageContentTabType;
  tabIndex: number;
  canDelete: boolean;
  isExpanded: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

const CMSTabContext = createContext<CMSTabContextType | null>(null);

type Props = PropsWithChildren<{
  tabIndex: number;
  tabType: SKUPageContentTabType;
}>;

export const CMSTabProvider: FC<Props> = ({ tabIndex, tabType, children }) => {
  const activeTabIndex = useCMSStore.use.activeTabIndex();
  const pageContent = useCMSStore.use.pageContent();

  const isExpanded = useMemo(
    () => tabIndex === activeTabIndex,
    [tabIndex, activeTabIndex],
  );

  const canMoveUp = useMemo(() => tabIndex !== 0, [tabIndex]);

  const canMoveDown = useMemo(() => {
    const isLastTab = tabIndex === size(pageContent?.data) - 1;

    return !isLastTab;
  }, [tabIndex, pageContent]);

  const canDelete = useMemo(() => {
    return size(pageContent?.data) > 1;
  }, [pageContent]);

  return (
    <CMSTabContext.Provider
      value={{
        tabType,
        tabIndex,
        isExpanded,
        canMoveUp,
        canMoveDown,
        canDelete,
      }}
    >
      {children}
    </CMSTabContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCMSTabContext = () => {
  const context = useContext(CMSTabContext);

  if (!context) {
    throw new Error("useCMSTabContext must be used within a CMSTabContext");
  }

  return context;
};
